import React from 'react';
import { AutoFocusedForm, notEmpty, PhotoField, TextField } from 'components';
import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  createStyles,
  Divider,
  Grid,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import { MutableButton } from 'ui/MutableButton';
import { RedButton } from 'components/Buttons';
import { Field } from 'react-final-form';
import { PremiumInfoQuery } from '../../../../schema';
import { useEditPremiumAccessForm } from './useEditPremiumAccessForm';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      minWidth: 500,
    },
    header: {
      fontWeight: 'bolder',
      marginBottom: '10px',
      marginTop: '20px',
    },
    content: {
      display: 'flex',
      gap: '20px',
      margin: '10px',
    },
    accessButton: {
      color: theme.palette.common.white,
      backgroundColor: theme.palette.success.dark,
      '&:hover': {
        color: theme.palette.success.dark,
        backgroundColor: theme.palette.success.light,
      },
    },
    actions: {
      padding: theme.spacing(2),
    },
  })
);

interface EditPremiumAccessFormProps {
  data: PremiumInfoQuery | undefined;
}

export const EditPremiumAccessForm = ({ data }: EditPremiumAccessFormProps) => {
  const styles = useStyles();
  const { initialValues, onSubmit, onCancel, loading } = useEditPremiumAccessForm({ data });

  return (
    <AutoFocusedForm
      onSubmit={onSubmit}
      initialValues={initialValues}
      render={({ handleSubmit, pristine }) => {
        return (
          <form onSubmit={handleSubmit}>
            <Card className={styles.root}>
              <CardHeader title="Редактирование Premium-доступа" />
              <Divider />
              <CardContent>
                <Typography variant="h6" component="h6" className={styles.header}>
                  Изображение
                </Typography>
                <Grid container spacing={4}>
                  <Grid item lg={4} md={4} xl={4} xs={12}>
                    <Field
                      name="backgroundImageId"
                      label="Фоновое изображение"
                      component={PhotoField}
                      url={data?.premiumInfo?.backgroundImage?.url}
                    />
                  </Grid>

                  <Grid item lg={8} md={8} xl={8} xs={12}>
                    <Typography variant="h6" component="h6" className={styles.header}>
                      Информация о блоке
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Field
                          name="title"
                          label="Заголовок"
                          component={TextField}
                          type="text"
                          validate={notEmpty}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          name="description"
                          label="Описание"
                          component={TextField}
                          type="text"
                          multiline
                          rows={4}
                          validate={notEmpty}
                        />
                      </Grid>
                    </Grid>
                    <Typography variant="h6" component="h6" className={styles.header}>
                      Кнопка действия
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Field
                          name="buttonText"
                          label="Текст кнопки"
                          component={TextField}
                          type="text"
                          validate={notEmpty}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Field
                          name="buttonActionCode"
                          label="Действие кнопки"
                          component={TextField}
                          type="text"
                          validate={notEmpty}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                <Divider style={{ margin: '20px 0' }} />

                <Typography variant="h6" component="h6" className={styles.header}>
                  Механика получения
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Field
                      name="mechanicsText"
                      label="Механика получения"
                      component={TextField}
                      type="text"
                      multiline
                      rows={6}
                      validate={notEmpty}
                    />
                  </Grid>
                </Grid>
              </CardContent>

              <CardActions className={styles.actions}>
                <MutableButton
                  className={styles.accessButton}
                  loading={loading}
                  color="primary"
                  disabled={pristine}
                  size="medium"
                  type="submit"
                  variant="contained"
                >
                  Сохранить
                </MutableButton>
                <RedButton
                  color="primary"
                  size="medium"
                  type="button"
                  variant="contained"
                  onClick={onCancel}
                >
                  Отменить
                </RedButton>
              </CardActions>
            </Card>
          </form>
        );
      }}
    />
  );
};
